import type { SxProps, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import {
	BRAND_ACCENT,
	BRAND_ACCENT_HOVER,
	BRAND_ACCENT_LIGHT,
	FORM_CONTROL_BORDER,
	FORM_CONTROL_BORDER_RADIUS,
	FORM_CONTROL_PADDING,
	FORM_INPUT_FONT_SIZE,
} from "@constants/brand.colors";

/** Maroon accent for inputs, selects, and chevron caps (Tailwind `accent`). */
export const formPrimary = BRAND_ACCENT;
export const formPrimaryHover = BRAND_ACCENT_HOVER;
export const formPrimaryMuted = BRAND_ACCENT_LIGHT;

/** Shared control height — aligned with institute Faculty forms. */
export const formControlHeight = 48;

export const formMenuItem = {
	hoverBg: BRAND_ACCENT_LIGHT,
	hoverColor: BRAND_ACCENT,
	selectedBg: BRAND_ACCENT,
	selectedColor: "#ffffff",
	selectedHoverBg: BRAND_ACCENT_HOVER,
} as const;

const borderError = "#d32f2f";

export const formFieldPlaceholderColor = "#9CA3AF";
export const formFieldSelectedColor = "#111827";
export const formSelectPlaceholderClassName = "student-select-placeholder";
export const formSelectValueClassName = "student-select-value";

const placeholderColor = formFieldPlaceholderColor;

export const formFieldBg = "#FFF";
export const formFieldBorder = FORM_CONTROL_BORDER;
export const formFieldBorderWidth = 1;
const fieldBorderActive = BRAND_ACCENT;

export const selectChevron = {
	width: 35,
	height: 36,
	borderRadius: 4,
	rightInset: 5,
} as const;

export const selectChevronCompact = {
	width: 30,
	height: 30,
	borderRadius: 4,
	rightInset: 4,
} as const;

/** 40px filter text fields — chevron cap fits inside control height */
export const selectChevronFilter = {
	width: 28,
	height: 28,
	borderRadius: 4,
	rightInset: 4,
} as const;

/** 36–38px filter multi-selects (closed) */
export const selectChevronFilterCompact = {
	width: 28,
	height: 28,
	borderRadius: 4,
	rightInset: 4,
} as const;

/** 32px filter multi-selects (closed) — inset chevron, not full field height */
export const selectChevronFilterDense = {
	width: 20,
	height: 20,
	borderRadius: 3,
	rightInset: 3,
} as const;

/** 35px filter multi-selects (closed) */
export const selectChevronFilterMid = {
	width: 24,
	height: 24,
	borderRadius: 3,
	rightInset: 4,
} as const;

/** Course list search + org filter — matches `PORTAL_COURSE_FIELD_ACTION_BTN` inset tile */
export const courseRowActionChevron = {
	width: 36,
	height: 32,
	borderRadius: 4,
	rightInset: 5,
} as const;

export const courseRowActionColor = "#ba2e5d";

export interface SelectChevronSpec {
	width: number;
	height: number;
	borderRadius: number;
	rightInset: number;
}

export const getSelectChevron = (controlHeight = formControlHeight): SelectChevronSpec => {
	if (controlHeight === 44) return courseRowActionChevron;
	if (controlHeight <= 32) return selectChevronFilterDense;
	if (controlHeight <= 35) return selectChevronFilterMid;
	if (controlHeight <= 38) return selectChevronFilterCompact;
	if (controlHeight <= 40) return selectChevronFilter;
	if (controlHeight < 48) return selectChevronCompact;
	return selectChevron;
};

export const getChevronReserve = (controlHeight = formControlHeight): number => {
	const chevron = getSelectChevron(controlHeight);
	return chevron.width + chevron.rightInset + 8;
};

export const selectChevronCapClassName =
	"absolute z-[2] flex cursor-pointer items-center justify-center border-0 bg-accent p-0 outline-none focus-visible:ring-2 focus-visible:ring-accent/40";

/** Muted chevron when the select is disabled — matches disabled text inputs. */
export const selectChevronCapDisabledClassName =
	"absolute z-[2] flex cursor-not-allowed items-center justify-center border-0 bg-[#E5E7EB] p-0 outline-none";

export const selectChevronDisabledIconColor = "#9CA3AF";

const disabledSelectBorderHoverSx: SystemStyleObject<Theme> = {
	"&:hover fieldset": {
		borderColor: "#e5e7eb !important",
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: "#e5e7eb !important",
	},
	"&.Mui-focused fieldset": {
		borderColor: "#e5e7eb !important",
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: "#e5e7eb !important",
	},
};

const fieldBorderSx = (error?: boolean): SystemStyleObject<Theme> => ({
	borderColor: error ? borderError : formFieldBorder,
	borderWidth: error ? 1.2 : formFieldBorderWidth,
});

const outlinedFieldBorderStates = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": fieldBorderSx(error),
	"& .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&:hover fieldset": {
		borderColor: error ? borderError : fieldBorderActive,
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: error ? borderError : fieldBorderActive,
	},
	"&.Mui-focused fieldset": {
		borderColor: error ? borderError : fieldBorderActive,
		borderWidth: error ? 1.2 : formFieldBorderWidth,
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: error ? borderError : fieldBorderActive,
		borderWidth: error ? 1.2 : formFieldBorderWidth,
	},
	"&.Mui-error fieldset": {
		borderColor: `${borderError} !important`,
	},
	"&.Mui-error .MuiOutlinedInput-notchedOutline": {
		borderColor: `${borderError} !important`,
	},
});

const resolveFieldBorderRadius = (height?: number) =>
	height === 44 ? "6px" : height !== undefined && height <= 32 ? "8px" : FORM_CONTROL_BORDER_RADIUS;

export const formOutlinedFieldShellSx = (
	error?: boolean,
	height?: number,
): SystemStyleObject<Theme> => ({
	backgroundColor: `${formFieldBg} !important`,
	borderRadius: resolveFieldBorderRadius(height),
	...outlinedFieldBorderStates(error),
});

export const outlinedInputExternalLabelProps = {
	notched: false,
} as const;

const selectOutlineNoNotchSx = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": {
		top: 0,
		...fieldBorderSx(error),
	},
	"& .MuiOutlinedInput-notchedOutline": {
		top: 0,
		...fieldBorderSx(error),
		"& legend": {
			display: "none",
			maxWidth: 0.01,
		},
	},
});

export const selectOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error, height),
	...selectOutlineNoNotchSx(error),
	minHeight: height,
	height,
	maxHeight: height,
	boxSizing: "border-box",
	paddingRight: `${getChevronReserve(height)}px !important`,
	overflow: "hidden",
	cursor: "pointer",
	alignItems: "center",
});

export const textOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error, height),
	minHeight: height,
	height,
});

const selectInnerSx = (height = formControlHeight): SystemStyleObject<Theme> => {
	if (height <= 38) {
		return {
			padding:
				height <= 32
					? "4px 6px 4px 8px"
					: height <= 35
						? "5px 7px 5px 9px"
						: height <= 36
							? "6px 8px 6px 10px"
							: "7px 8px 7px 10px",
			fontSize: "12px",
			fontWeight: 400,
			lineHeight: 1.2,
			display: "flex",
			alignItems: "center",
			fontFamily: "Poppins, sans-serif",
			minHeight: "unset",
			height: "100%",
			maxHeight: "100%",
			boxSizing: "border-box",
			color: formFieldPlaceholderColor,
			backgroundColor: "transparent",
			cursor: "pointer",
		};
	}

	return {
		padding:
			height === 44
				? "5px 8px 5px 8px"
				: height <= 36
					? "6px 8px 6px 10px"
					: height <= 40
						? "8px 8px 8px 12px"
						: "13px 8px 13px 15px",
		fontSize: height <= 36 ? "12px" : FORM_INPUT_FONT_SIZE,
		fontWeight: 400,
		lineHeight: height <= 36 ? 1.25 : 1.5,
		display: "flex",
		alignItems: "center",
		fontFamily: "Poppins, sans-serif",
		minHeight: height,
		height,
		boxSizing: "border-box",
		color: formFieldPlaceholderColor,
		backgroundColor: "transparent",
		cursor: "pointer",
	};
};

export const selectControlSx = (error?: boolean, height = formControlHeight): SxProps<Theme> => {
	const outlined = selectOutlinedInputSx(error, height);
	return {
		"&.MuiOutlinedInput-root": outlined,
		"& .MuiOutlinedInput-root": outlined,
		"& .MuiSelect-select": selectInnerSx(height),
		"& .MuiSelect-icon": {
			display: "none",
		},
		"&.Mui-disabled": {
			backgroundColor: "#f3f4f6 !important",
			cursor: "not-allowed",
			...disabledSelectBorderHoverSx,
		},
		"&.MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: "#f3f4f6 !important",
			cursor: "not-allowed",
			...disabledSelectBorderHoverSx,
		},
		"& .MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: "#f3f4f6 !important",
			cursor: "not-allowed",
			...disabledSelectBorderHoverSx,
		},
		"& .MuiOutlinedInput-root.Mui-disabled fieldset": {
			borderColor: "#e5e7eb !important",
		},
		"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
			borderColor: "#e5e7eb !important",
		},
		"& .MuiSelect-select.Mui-disabled": {
			WebkitTextFillColor: "#6B7280",
			color: "#6B7280",
			opacity: 1,
			cursor: "not-allowed",
		},
		"& .MuiSelect-select.Mui-disabled span": {
			color: "#6B7280 !important",
			WebkitTextFillColor: "#6B7280 !important",
		},
		[`& .MuiSelect-select .${formSelectPlaceholderClassName}`]: {
			color: `${formFieldPlaceholderColor} !important`,
			WebkitTextFillColor: `${formFieldPlaceholderColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectValueClassName}`]: {
			color: `${formFieldSelectedColor} !important`,
			WebkitTextFillColor: `${formFieldSelectedColor} !important`,
		},
		[`& .MuiSelect-select.Mui-disabled .${formSelectValueClassName}`]: {
			color: "#6B7280 !important",
			WebkitTextFillColor: "#6B7280 !important",
		},
	};
};

const textInputInnerBase: SystemStyleObject<Theme> = {
	fontFamily: "Poppins, sans-serif",
	fontSize: FORM_INPUT_FONT_SIZE,
	fontWeight: 400,
	color: "#111827",
	boxSizing: "border-box",
	backgroundColor: "transparent",
};

export type TextFieldControlSize = number | { height?: string; padding?: string };

const resolveCustomFieldHeight = (height?: string): number => {
	if (!height) return formControlHeight;
	const parsed = parseInt(String(height), 10);
	if (!Number.isFinite(parsed) || parsed < 32) return formControlHeight;
	return parsed;
};

export const textFieldControlSx = (error?: boolean, size?: TextFieldControlSize): SxProps<Theme> => {
	if (typeof size === "object" && size !== null) {
		const height = resolveCustomFieldHeight(size.height);
		return {
			"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
			"& input, & textarea": {
				...textInputInnerBase,
				padding: size.padding ?? FORM_CONTROL_PADDING,
				height,
				minHeight: height,
			},
			"& input::placeholder, & textarea::placeholder": {
				color: placeholderColor,
				opacity: 1,
			},
		};
	}

	const height = typeof size === "number" ? size : formControlHeight;

	return {
		"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
		"& input, & textarea": {
			...textInputInnerBase,
			padding: FORM_CONTROL_PADDING,
			height,
		},
		"& input::placeholder, & textarea::placeholder": {
			color: placeholderColor,
			opacity: 1,
		},
	};
};

export const mergeSelectSx = (
	error?: boolean,
	height?: number,
	...overrides: (SxProps<Theme> | undefined)[]
): SxProps<Theme> => [selectControlSx(error, height), ...overrides.filter(Boolean)] as SxProps<Theme>;

export const defaultSelectMenuProps = {
	PaperProps: {
		sx: {
			borderRadius: 2,
			boxShadow: "0 2px 8px 0 rgba(16, 24, 40, 0.04)",
			mt: 1,
		},
	},
	sx: {
		zIndex: 10001,
	},
} as const;

export { placeholderColor };
